
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Upload, Image as ImageIcon } from "lucide-react";

interface VehicleImageUploadProps {
  onImageUploaded: (url: string) => void;
}

const VehicleImageUpload = ({ onImageUploaded }: VehicleImageUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast({
        variant: "destructive",
        title: "Ongeldig bestandstype",
        description: "Upload alstublieft alleen afbeeldingen.",
      });
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "Bestand te groot",
        description: "De afbeelding mag maximaal 5MB zijn.",
      });
      return;
    }

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError, data } = await supabase.storage
        .from('vehicle_images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('vehicle_images')
        .getPublicUrl(filePath);

      onImageUploaded(publicUrl);

      toast({
        title: "Afbeelding geüpload",
        description: "De afbeelding is succesvol geüpload.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Fout bij uploaden",
        description: error.message,
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center space-y-4">
      <div className="relative">
        <input
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isUploading}
        />
        <Button
          variant="outline"
          className="w-full h-32 border-dashed flex flex-col items-center justify-center space-y-2"
          disabled={isUploading}
        >
          {isUploading ? (
            <>
              <Upload className="h-8 w-8 animate-pulse" />
              <span>Uploaden...</span>
            </>
          ) : (
            <>
              <ImageIcon className="h-8 w-8" />
              <span>Klik om een afbeelding te uploaden</span>
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default VehicleImageUpload;
